/**
 * Email Signature API service for ATS (per-user, workspace-scoped).
 */

import { createApiClient } from '@aaas/shared';
import type {
  EmailSignatureCreateInput,
  EmailSignatureListResponse,
  EmailSignatureResponse,
  EmailSignatureUpdateInput,
} from '../types';

interface ListSignaturesOptions {
  skip?: number;
  limit?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

class EmailSignatureService {
  async listSignatures(
    workspaceId: string,
    idToken: string,
    options: ListSignaturesOptions = {}
  ): Promise<EmailSignatureListResponse> {
    const { skip = 0, limit = 50, sortBy, order } = options;
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailSignatureListResponse>('/email-signatures', {
      params: {
        skip,
        limit,
        sort_by: sortBy || undefined,
        order: order || undefined,
      },
      headers: { 'X-Workspace-ID': workspaceId },
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list email signatures');
    }
    return response.data;
  }

  async getSignature(
    signatureId: string,
    workspaceId: string,
    idToken: string
  ): Promise<EmailSignatureResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<EmailSignatureResponse>(
      `/email-signatures/${signatureId}`,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get email signature');
    }
    return response.data;
  }

  async createSignature(
    workspaceId: string,
    idToken: string,
    data: EmailSignatureCreateInput
  ): Promise<EmailSignatureResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<EmailSignatureResponse>(
      '/email-signatures',
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create email signature');
    }
    return response.data;
  }

  async updateSignature(
    signatureId: string,
    workspaceId: string,
    idToken: string,
    data: EmailSignatureUpdateInput
  ): Promise<EmailSignatureResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<EmailSignatureResponse>(
      `/email-signatures/${signatureId}`,
      data,
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update email signature');
    }
    return response.data;
  }

  async setDefault(
    signatureId: string,
    workspaceId: string,
    idToken: string
  ): Promise<EmailSignatureResponse> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<EmailSignatureResponse>(
      `/email-signatures/${signatureId}/set-default`,
      {},
      { headers: { 'X-Workspace-ID': workspaceId } }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to set default signature');
    }
    return response.data;
  }

  async deleteSignature(
    signatureId: string,
    workspaceId: string,
    idToken: string
  ): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/email-signatures/${signatureId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

export default new EmailSignatureService();
